// components/site/SiteChrome.js
// Shared terminal-theme chrome for the public pages (Reports, Learn, Legal,
// Contact): fonts + type classes, header, and footer. Matches LandingPage.js.
// Palette: abyss #07131F · panel #0D1E2E · line #1C3348 · text #DCE6F0 ·
// action #5AA9F0 (hover #8CC4F5) · target red #F0414F.
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Menu, X, Mail, ExternalLink, Lock, Shield, Star, PlayCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import tunaLogo from '../../assets/tunasignalai_logo.png';

export const WEBULL_EXTENSION_URL = 'https://chromewebstore.google.com/detail/dhgdpcbphhlnecgklanbhbcebidhddbg';
export const TRUSTPILOT_URL = 'https://www.trustpilot.com/review/tunasignal.ai';

// Primary / secondary button classes, shared so every page matches.
export const BTN_PRIMARY = 'inline-flex items-center justify-center px-4 py-2 rounded-md bg-[#5AA9F0] text-[#07131F] font-semibold hover:bg-[#8CC4F5] transition-colors';
export const BTN_SECONDARY = 'inline-flex items-center justify-center px-4 py-2 rounded-md border border-[#28445F] text-[#DCE6F0] hover:bg-[#13283C] transition-colors';

export const SiteStyles = () => (
  <Helmet>
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
    <link
      rel="stylesheet"
      href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500;600;700&display=swap"
    />
    <style>{`
      .ts-page { font-family: 'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif; color: #DCE6F0; background: #07131F; }
      .ts-display { font-family: 'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif; font-weight: 600; letter-spacing: -0.025em; line-height: 1.08; }
      .ts-display-sub { font-family: 'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif; font-weight: 600; letter-spacing: -0.015em; line-height: 1.2; }
      .ts-num { font-family: 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace; font-variant-numeric: tabular-nums; }
      .ts-page a:focus-visible, .ts-page button:focus-visible, .ts-page input:focus-visible, .ts-page textarea:focus-visible { outline: 2px solid #5AA9F0; outline-offset: 2px; }
    `}</style>
  </Helmet>
);

const NAV = [
  { key: 'features', label: 'Features', to: '/#features' },
  { key: 'trial', label: 'Free Trial', to: '/#trial-rewards' },
  { key: 'pricing', label: 'Pricing', to: '/#pricing' },
  { key: 'reports', label: 'Reports', to: '/reports' },
  { key: 'learn', label: 'Learn', to: '/learn' },
  { key: 'contact', label: 'Contact', to: '/contact' },
];

export const SiteHeader = ({ active }) => {
  const { currentUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const linkCls = (key) => (key === active
    ? 'text-white font-semibold'
    : 'text-slate-300 hover:text-white');

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || open ? 'bg-[#07131F]/95 backdrop-blur-md border-b border-[#1C3348]' : 'bg-transparent'
      }`}>
        <nav className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-label="Main">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-3">
              <img src={tunaLogo} alt="TunaSignal AI" className="w-10 h-10" />
              <div className="flex flex-col">
                <span className="text-xl font-bold tracking-tight text-white">
                  TunaSignal <span className="text-sky-400">AI</span>
                </span>
                <span className="hidden sm:block text-xs text-slate-400">Find movers early. Know if you're still early.</span>
              </div>
            </Link>

            <div className="hidden md:flex items-center space-x-7">
              {NAV.map((n) => (
                <Link key={n.key} to={n.to} className={`transition-colors ${linkCls(n.key)}`} aria-current={n.key === active ? 'page' : undefined}>
                  {n.label}
                </Link>
              ))}
            </div>

            <div className="flex items-center space-x-3">
              <div className="hidden md:flex items-center space-x-3">
                {currentUser ? (
                  <Link to="/stockwatch" className={BTN_PRIMARY}>Back to Stock Watch</Link>
                ) : (
                  <>
                    <Link to="/login" className="px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-[#13283C] transition-colors">Sign In</Link>
                    <Link to="/register" className={BTN_PRIMARY}>Get Started</Link>
                  </>
                )}
              </div>
              <button
                onClick={() => setOpen(!open)}
                className="md:hidden p-2 rounded-md text-slate-300 hover:bg-[#13283C]"
                aria-label={open ? 'Close menu' : 'Open menu'}
                aria-expanded={open}
              >
                {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {open && (
            <div className="md:hidden absolute top-full left-0 right-0 z-50 bg-[#07131F] border-b border-[#1C3348]">
              <div className="flex flex-col space-y-1 px-4 py-4">
                {NAV.map((n) => (
                  <Link key={n.key} to={n.to} onClick={() => setOpen(false)}
                    className={`px-4 py-3 rounded-md hover:bg-[#13283C] ${linkCls(n.key)}`}>
                    {n.label}
                  </Link>
                ))}
                <div className="my-3 border-t border-[#1C3348]" />
                {currentUser ? (
                  <Link to="/stockwatch" onClick={() => setOpen(false)} className={`${BTN_PRIMARY} py-3`}>Back to Stock Watch</Link>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setOpen(false)} className={`${BTN_SECONDARY} py-3`}>Sign In</Link>
                    <Link to="/register" onClick={() => setOpen(false)} className={`${BTN_PRIMARY} py-3 mt-2`}>Get Started</Link>
                  </>
                )}
              </div>
            </div>
          )}
        </nav>
      </header>
      {open && <div className="md:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setOpen(false)} />}
    </>
  );
};

const SOCIAL = [
  { label: 'X (Twitter)', href: 'https://x.com/TunaSignalAI', path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' },
  { label: 'Instagram', href: 'https://www.instagram.com/tunasignal.ai/', path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z' },
  { label: 'TikTok', href: 'https://www.tiktok.com/@tunasignalai', path: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z' },
  { label: 'YouTube', href: 'https://www.youtube.com/@TunaSignalAI', path: 'M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z' },
];

const fLink = 'text-sm text-slate-400 hover:text-white transition-colors';

export const SiteFooter = () => (
  <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-[#1C3348] bg-[#07131F]">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center space-x-2 mb-4">
            <img src={tunaLogo} alt="TunaSignal AI" className="w-8 h-8" />
            <span className="font-bold text-lg text-white">TunaSignal AI</span>
          </div>
          <p className="text-sm mb-4 text-slate-400">Find movers early. Know if you're still early.</p>
          <p className="text-sm text-slate-400"><strong>Important:</strong> We are not financial advisors. All investments involve risk.</p>
          <div className="mt-5">
            <p className="text-xs font-semibold mb-2 text-slate-500">Follow us</p>
            <div className="flex space-x-4">
              {SOCIAL.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={`TunaSignalAI on ${s.label}`} title={s.label}
                  className="text-slate-400 hover:text-white transition-colors">
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true"><path d={s.path} /></svg>
                </a>
              ))}
              <a href={TRUSTPILOT_URL} target="_blank" rel="noopener noreferrer" aria-label="TunaSignalAI reviews on Trustpilot" title="Trustpilot reviews"
                className="text-slate-400 hover:text-white transition-colors">
                <Star className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-semibold mb-4 text-white">Product</h4>
          <ul className="space-y-2">
            <li><Link to="/#features" className={fLink}>Features</Link></li>
            <li>
              <a href={WEBULL_EXTENSION_URL} target="_blank" rel="noopener noreferrer" className={`${fLink} inline-flex items-center`}>
                Webull Sync Extension <ExternalLink className="w-3 h-3 ml-1" />
              </a>
            </li>
            <li><Link to="/#trial-rewards" className={fLink}>Trial Rewards</Link></li>
            <li><Link to="/#pricing" className={fLink}>Pricing</Link></li>
            <li><Link to="/reports" className={fLink}>Performance Reports</Link></li>
            <li><Link to="/learn#livewatch" className={fLink}>Live Watch Guide</Link></li>
            <li><Link to="/learn#shortwatch" className={fLink}>Short Watch Guide</Link></li>
            <li><Link to="/learn#replay" className={fLink}>Replay Guide</Link></li>
            <li>
              <Link to="/learn#academy" className={`${fLink} inline-flex items-center`}>
                Academy course <PlayCircle className="w-3 h-3 ml-1" />
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold mb-4 text-white">Support</h4>
          <ul className="space-y-2">
            <li><Link to="/contact" className={fLink}>Contact Us</Link></li>
            <li>
              <a href="mailto:support@tunasignal.ai" className={`${fLink} inline-flex items-center`}>
                <Mail className="w-3 h-3 mr-1" /> support@tunasignal.ai
              </a>
            </li>
            <li>
              <a href={TRUSTPILOT_URL} target="_blank" rel="noopener noreferrer" className={`${fLink} inline-flex items-center`}>
                Reviews on Trustpilot <ExternalLink className="w-3 h-3 ml-1" />
              </a>
            </li>
            <li><span className="text-sm text-slate-400">Cancel anytime from your profile</span></li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold mb-4 text-white">Legal</h4>
          <ul className="space-y-2">
            <li><Link to="/legal#privacy" className={fLink}>Privacy Policy</Link></li>
            <li><Link to="/legal#terms" className={fLink}>Terms &amp; Conditions</Link></li>
            <li><Link to="/legal#refund" className={fLink}>Refund Policy</Link></li>
            <li><Link to="/legal#webull-extension-privacy" className={fLink}>Extension Privacy</Link></li>
          </ul>
        </div>
      </div>

      <div className="pt-8 border-t border-[#1C3348] flex flex-col md:flex-row justify-between items-center gap-4">
        <div>
          <p className="text-sm text-slate-400">&copy; {new Date().getFullYear()} TunaSignal AI, Wyoming LLC. All rights reserved.</p>
          <p className="text-xs mt-1 text-slate-500">TunaSignal AI, LLC, 30 N Gould St Ste N, Sheridan, WY 82801</p>
        </div>
        <div className="flex items-center space-x-4">
          <span className="text-xs text-slate-500">Payments handled by Stripe</span>
        </div>
      </div>
    </div>
  </footer>
);
